import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  CardActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  FormHelperText,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

import { RootState } from "../../store/Store";
import {
  fetchData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../components/Methods/SnackBar";
import Navbar from "../../components/Page Component/Navbar";
import Footer from "../../components/Page Component/Footer";
import { setIsLoading } from "../../store/LoadingSlice";
import { userComplianceAssignmentsUrl } from "../../Service/Apis";
import { titleCase } from "./utils";

interface ComplianceAssignment {
  assignment_id: string;
  project_name: string;
  template_name: string;
  due_date: string;
  due_date_str: string;
  status: "pending" | "in_progress" | "completed" | "overdue";
  month_year_str: string;
  compliance_rate: number;
}

const ManagerCompliance: React.FC = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<ComplianceAssignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<
    ComplianceAssignment[]
  >([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [monthYearFilter, setMonthYearFilter] = useState<string>("");
  const [availableMonthYears, setAvailableMonthYears] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  useEffect(() => {
    fetchAssignments();
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = [...assignments];

    if (statusFilter) {
      filtered = filtered.filter(
        (assignment) => assignment.status === statusFilter
      );
    }

    if (monthYearFilter) {
      filtered = filtered.filter(
        (assignment) => assignment.month_year_str === monthYearFilter
      );
    }

    setFilteredAssignments(filtered);
  }, [statusFilter, monthYearFilter, assignments]);

  const fetchAssignments = async () => {
    try {
      dispatch(setIsLoading(true));
      setLoading(true);

      const response = await dispatch2(
        fetchData(`${userComplianceAssignmentsUrl}?role=${selectedRole}`)
      );

      const assignmentData = response as ComplianceAssignment[];
      setAssignments(assignmentData);
      setFilteredAssignments(assignmentData);

      // Extract unique month/years for filter
      const uniqueMonthYears = [
        ...new Set(assignmentData.map((a) => a.month_year_str)),
      ];
      setAvailableMonthYears(uniqueMonthYears);

      if (assignmentData.length === 0) {
        showToast("No compliance assignments found", "info", true);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
      showToast("Error fetching your compliance assignments", "error", true);
    } finally {
      dispatch(setIsLoading(false));
      setLoading(false);
    }
  };

  const navigateToForm = (assignmentId: string) => {
    navigate(`/process-compliance-form/${assignmentId}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "#4caf50";
      case "in_progress":
        return "#2196f3";
      case "pending":
        return "#ff9800";
      case "overdue":
        return "#f44336";
      default:
        return "#757575";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <AssignmentTurnedInIcon />;
      case "in_progress":
        return <AssignmentIcon />;
      case "pending":
        return <PendingActionsIcon />;
      case "overdue":
        return <AssignmentLateIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  // Calculate counts for summary
  const pendingCount = assignments.filter((a) => a.status === "pending").length;
  const overdueCount = assignments.filter((a) => a.status === "overdue").length;
  const inProgressCount = assignments.filter(
    (a) => a.status === "in_progress"
  ).length;
  const completedCount = assignments.filter(
    (a) => a.status === "completed"
  ).length;

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 12, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h5" fontWeight="bold" mb={3}>
            My Compliance Assignments
          </Typography>

          {/* Summary cards */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%", bgcolor: "#f9f9f9" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      Pending
                    </Typography>
                    <PendingActionsIcon color="warning" />
                  </Box>
                  <Typography variant="h4">{pendingCount}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%", bgcolor: "#f9f9f9" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      In Progress
                    </Typography>
                    <AssignmentIcon color="info" />
                  </Box>
                  <Typography variant="h4">{inProgressCount}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%", bgcolor: "#f9f9f9" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      Completed
                    </Typography>
                    <AssignmentTurnedInIcon color="success" />
                  </Box>
                  <Typography variant="h4">{completedCount}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%", bgcolor: "#f9f9f9" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      Overdue
                    </Typography>
                    <AssignmentLateIcon color="error" />
                  </Box>
                  <Typography variant="h4">{overdueCount}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">
                  Filter by Status
                </InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="month-filter-label">
                  Filter by Period
                </InputLabel>
                <Select
                  labelId="month-filter-label"
                  value={monthYearFilter}
                  label="Filter by Period"
                  onChange={(e) => setMonthYearFilter(e.target.value)}
                >
                  <MenuItem value="">All Periods</MenuItem>
                  {availableMonthYears.map((monthYear) => (
                    <MenuItem key={monthYear} value={monthYear}>
                      {monthYear}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* Content */}
          {loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}
            >
              <CircularProgress />
            </Box>
          ) : filteredAssignments.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No compliance assignments match your filters. Try changing the
              filters or check back later.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {filteredAssignments.map((assignment) => (
                <Grid item xs={12} md={6} key={assignment.assignment_id}>
                  <Card sx={{ height: "100%", position: "relative" }}>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "10px",
                        height: "100%",
                        backgroundColor: getStatusColor(assignment.status),
                      }}
                    />
                    <CardContent sx={{ pb: 1 }}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="h6" fontWeight="medium" noWrap>
                          {assignment.project_name}
                        </Typography>
                        <Chip
                          label={titleCase(assignment.status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(assignment.status),
                            color: "white",
                          }}
                          icon={
                            <Box
                              sx={{
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                mr: -0.5,
                              }}
                            >
                              {getStatusIcon(assignment.status)}
                            </Box>
                          }
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {assignment.template_name}
                      </Typography>

                      <Box display="flex" alignItems="center" mt={2} mb={1}>
                        <CalendarTodayIcon
                          fontSize="small"
                          color="action"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2">
                          Period: <strong>{assignment.month_year_str}</strong>
                        </Typography>
                      </Box>

                      <Typography variant="body2">
                        Due:{" "}
                        <strong>
                          {assignment.due_date_str || assignment.due_date}
                        </strong>
                      </Typography>

                      {assignment.compliance_rate !== null && (
                        <Box display="flex" alignItems="center" mt={1}>
                          <Typography variant="body2" mr={1}>
                            Completion:
                          </Typography>
                          <Box
                            sx={{
                              position: "relative",
                              display: "inline-flex",
                            }}
                          >
                            <CircularProgress
                              variant="determinate"
                              value={assignment.compliance_rate}
                              size={24}
                              sx={{
                                color:
                                  assignment.compliance_rate === 100
                                    ? "#4caf50"
                                    : assignment.compliance_rate > 0
                                    ? "#2196f3"
                                    : "#ff9800",
                              }}
                            />
                            <Box
                              sx={{
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                position: "absolute",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography variant="caption" fontSize={10}>
                                {`${Math.round(assignment.compliance_rate)}%`}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => navigateToForm(assignment.assignment_id)}
                        variant="contained"
                        color={
                          assignment.status === "completed"
                            ? "primary"
                            : "primary"
                        }
                      >
                        {assignment.status === "completed"
                          ? "View Form"
                          : "Fill Form"}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default ManagerCompliance;
